import { Alert, Paper, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Practitioner, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { Loading, QuestionnaireForm, useMedplum, useMedplumProfile } from '@medplum/react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader } from '../../components/layout/PageHeader';
import { botIdentifier } from '../../lib/bot-identifier';
import { showErrorNotification } from '../../utils/notifications';
import { withTenantScope } from '../../utils/tenant';

export function InviteProviderPage(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    medplum
      .searchOne('Questionnaire', { name: 'invite-provider' })
      .then((result) => {
        setQuestionnaire(result);
      })
      .catch((error) => {
        showErrorNotification(error);
      })
      .finally(() => setLoading(false));
  }, [medplum]);

  const handleOnSubmit = useCallback(
    async (response: QuestionnaireResponse) => {
      if (!questionnaire || !profile) {
        return;
      }
      try {
        const practitioner: Practitioner = await medplum.executeBot(
          botIdentifier('invite-provider'),
          withTenantScope(response)
        );

        showNotification({
          color: 'green',
          message: `${practitioner.name?.[0]?.given?.[0] ?? ''} ${practitioner.name?.[0]?.family ?? ''} has been invited successfully`,
        });

        await navigate('/admin/staffing');
      } catch (error) {
        showNotification({
          color: 'red',
          message: normalizeErrorString(error),
          autoClose: false,
        });
      }
    },
    [medplum, navigate, profile, questionnaire]
  );

  if (loading) {
    return <Loading />;
  }

  if (!questionnaire) {
    return (
      <Stack gap="md" p="md">
        <PageHeader title="Invite Provider" />
        <Alert color="red" title="Questionnaire not found">
          The invite provider questionnaire could not be found. Please contact support.
        </Alert>
      </Stack>
    );
  }

  const questionnaireWithoutTitle = { ...questionnaire, title: undefined };

  return (
    <Stack gap="md" p="md">
      <PageHeader title="Invite Provider" />
      <Paper shadow="sm" withBorder p="lg" radius="md" bg="white">
        <QuestionnaireForm questionnaire={questionnaireWithoutTitle} onSubmit={handleOnSubmit} />
      </Paper>
    </Stack>
  );
}
